import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Users, UserCheck, UserX, Clock, AlertTriangle,
  ChevronLeft, ChevronRight, Calendar, Loader2,
  TrendingUp, Timer
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  presente: { 
    label: 'Presente', 
    cor: 'bg-emerald-500', 
    textCor: 'text-emerald-600',
    bgLight: 'bg-emerald-50',
    icon: UserCheck 
  },
  ausente: { 
    label: 'Ausente', 
    cor: 'bg-red-500', 
    textCor: 'text-red-600',
    bgLight: 'bg-red-50',
    icon: UserX 
  },
  atraso: { 
    label: 'Atraso', 
    cor: 'bg-amber-500', 
    textCor: 'text-amber-600',
    bgLight: 'bg-amber-50',
    icon: Clock 
  },
  hora_extra: { 
    label: 'Hora Extra', 
    cor: 'bg-purple-500', 
    textCor: 'text-purple-600',
    bgLight: 'bg-purple-50',
    icon: TrendingUp 
  }
};

const UnifiedQuadroPresenca = () => {
  const navigate = useNavigate();
  const { jornyxToken, jornyxUser } = useUnifiedAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(new Date().toISOString().split('T')[0]);
  const [quadro, setQuadro] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    presentes: 0,
    ausentes: 0,
    atrasados: 0,
    hora_extra: 0,
    taxa_presenca: 0
  });
  const [filtro, setFiltro] = useState('todos');

  useEffect(() => {
    if (jornyxToken) {
      carregarQuadro();
    }
  }, [jornyxToken, data]);

  const carregarQuadro = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/app/quadro-presenca?data=${data}`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const dados = await res.json();
        setQuadro(dados.quadro || []);
        setEstatisticas(dados.estatisticas || {});
      } else {
        toast.error('Erro ao carregar quadro de presença');
      }
    } catch (err) {
      console.error('Erro:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    try {
      const d = new Date(dataStr + 'T12:00:00');
      return d.toLocaleDateString('pt-BR', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dataStr;
    }
  };

  const mudarData = (dias) => {
    const novaData = new Date(data + 'T12:00:00');
    novaData.setDate(novaData.getDate() + dias);
    setData(novaData.toISOString().split('T')[0]);
  };

  const formatarMinutos = (min) => {
    if (!min) return '-';
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h > 0) {
      return `${h}h${m > 0 ? m + 'min' : ''}`;
    }
    return `${m}min`;
  };

  const quadroFiltrado = quadro.filter(item => {
    if (filtro === 'todos') return true;
    return item.status === filtro;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/unified')}
          className="p-2"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">Quadro de Presença</h2>
          <p className="text-sm text-slate-500">Visão diária da equipe</p>
        </div>
      </div>
      
      {/* Seletor de Data */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => mudarData(-1)}
              className="p-2"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex-1 text-center">
              <Input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="text-center border-0 bg-transparent text-lg font-semibold"
              />
              <p className="text-xs text-slate-500 mt-1 capitalize">
                {formatarData(data)}
              </p>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => mudarData(1)}
              className="p-2"
              disabled={data >= new Date().toISOString().split('T')[0]}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 gap-3">
        <Card className={`border-0 shadow-sm ${filtro === 'presente' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => setFiltro(filtro === 'presente' ? 'todos' : 'presente')}
        >
          <CardContent className="py-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{estatisticas.presentes}</p>
                <p className="text-xs text-slate-500">Presentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-0 shadow-sm ${filtro === 'ausente' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFiltro(filtro === 'ausente' ? 'todos' : 'ausente')}
        >
          <CardContent className="py-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{estatisticas.ausentes}</p>
                <p className="text-xs text-slate-500">Ausentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-0 shadow-sm ${filtro === 'atraso' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setFiltro(filtro === 'atraso' ? 'todos' : 'atraso')}
        >
          <CardContent className="py-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{estatisticas.atrasados}</p>
                <p className="text-xs text-slate-500">Atrasados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-0 shadow-sm ${filtro === 'hora_extra' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setFiltro(filtro === 'hora_extra' ? 'todos' : 'hora_extra')}
        >
          <CardContent className="py-4 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{estatisticas.hora_extra}</p>
                <p className="text-xs text-slate-500">Hora Extra</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Taxa de Presença */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Taxa de Presença</p>
                <p className="text-xs text-slate-400">{estatisticas.total} empregados</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{estatisticas.taxa_presenca}%</p>
            </div>
          </div>
          {/* Barra de progresso */}
          <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
              style={{ width: `${estatisticas.taxa_presenca}%` }}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Filtro ativo */}
      {filtro !== 'todos' && (
        <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
          <span className="text-sm text-slate-600">
            Filtro: <strong className="capitalize">{STATUS_CONFIG[filtro]?.label || filtro}</strong>
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setFiltro('todos')}
            className="text-xs"
          >
            Limpar
          </Button>
        </div>
      )}
      
      {/* Lista de Empregados */}
      <div className="space-y-3">
        {quadroFiltrado.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {filtro !== 'todos' 
                  ? 'Nenhum empregado com este status'
                  : 'Nenhum empregado encontrado'}
              </p>
            </CardContent>
          </Card>
        ) : (
          quadroFiltrado.map((item) => {
            const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.ausente;
            const StatusIcon = config.icon;
            
            return (
              <Card 
                key={item.empregado_id} 
                className={`border-0 shadow-sm hover:shadow-md transition-all ${config.bgLight}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${config.cor} rounded-full`}>
                        <StatusIcon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{item.nome}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {item.matricula && <span>Mat: {item.matricula}</span>}
                          {item.cargo && <span>• {item.cargo}</span>}
                        </div>
                      </div>
                    </div>
                    <Badge className={`${config.cor} text-white`}>
                      {config.label}
                    </Badge>
                  </div>
                  
                  {/* Detalhes de horário */}
                  <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-slate-400">Entrada</p>
                      <p className="text-sm font-medium text-slate-700">
                        {item.primeira_entrada || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Saída</p>
                      <p className="text-sm font-medium text-slate-700">
                        {item.ultima_saida || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">
                        {item.atraso_minutos > 0 ? 'Atraso' : item.hora_extra_minutos > 0 ? 'H.Extra' : 'Marcações'}
                      </p>
                      <p className={`text-sm font-medium ${
                        item.atraso_minutos > 0 ? 'text-amber-600' : 
                        item.hora_extra_minutos > 0 ? 'text-purple-600' : 'text-slate-700'
                      }`}>
                        {item.atraso_minutos > 0 
                          ? formatarMinutos(item.atraso_minutos)
                          : item.hora_extra_minutos > 0 
                            ? formatarMinutos(item.hora_extra_minutos)
                            : item.total_marcacoes
                        }
                      </p>
                    </div>
                  </div>
                  
                  {/* Inconsistências */}
                  {item.inconsistencias_pendentes > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-amber-600 text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      <span>{item.inconsistencias_pendentes} inconsistência(s) pendente(s)</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UnifiedQuadroPresenca;
